import React, { useState, useEffect, useCallback } from 'react';

const WORKER_URL = 'https://rakeen-whatsapp-poc.thamer-21d.workers.dev';

const STATUS_FLOW = ['inquiry', 'quoted', 'confirmed', 'processing', 'ready', 'delivered'];
const STATUS_LABELS = {
  inquiry: 'استفسار',
  quoted: 'عرض سعر',
  confirmed: 'تأكيد',
  processing: 'تجهيز',
  ready: 'جاهز',
  delivered: 'تسليم',
};

const FILTERS = [
  { id: 'all', label: 'الكل' },
  { id: 'customers', label: 'العملاء فقط' },
  { id: 'test', label: 'الاختبار فقط' },
];

// ─── Order Card ───

function OrderCard({ order, onAdvance, onReply, onQuote, onToggleTest, onDelete, advancing, sending }) {
  const [replyText, setReplyText] = useState('');
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [quoteData, setQuoteData] = useState({ unitPrice: '', quantity: '', delivery: '3-5 أيام عمل', notes: '' });

  const currentIndex = STATUS_FLOW.indexOf(order.status);
  const nextStatus = STATUS_FLOW[currentIndex + 1];
  const isOrderType = order.tag && (order.tag.includes('عرض سعر') || order.tag.includes('صيانة'));
  const canQuote = isOrderType && order.status === 'inquiry';

  const handleReply = () => {
    if (!replyText.trim()) return;
    onReply(order.id, replyText.trim());
    setReplyText('');
  };

  const handleQuote = () => {
    if (!quoteData.unitPrice || !quoteData.quantity) return;
    onQuote(order.id, {
      unitPrice: parseFloat(quoteData.unitPrice),
      quantity: parseInt(quoteData.quantity, 10),
      delivery: quoteData.delivery,
      notes: quoteData.notes,
    });
    setShowQuoteForm(false);
    setQuoteData({ unitPrice: '', quantity: '', delivery: '3-5 أيام عمل', notes: '' });
  };

  return (
    <div className={`sd-conv-card ${order.isTest ? 'sd-conv-test' : ''}`}>
      <div className="sd-conv-header">
        <span className="sd-conv-phone">{order.phone}</span>
        <span className="sd-conv-id">#{order.id}</span>
        <span className="sd-conv-tag">{order.tag}</span>
        {order.isTest && <span className="sd-conv-test-badge">🧪 اختبار</span>}
        <button
          className="sd-conv-toggle-test"
          onClick={() => onToggleTest(order.id)}
          title={order.isTest ? 'تحويل لعميل' : 'تحويل لاختبار'}
        >
          {order.isTest ? '👤 عميل' : '🧪 اختبار'}
        </button>
        <button
          className="sd-conv-delete"
          onClick={() => onDelete(order.id)}
          title="حذف المحادثة"
        >🗑️</button>
      </div>

      <div className="sd-conv-messages">
        {order.messages.map((msg, i) => (
          <div key={i} className={`sd-conv-msg sd-conv-msg-${msg.sender}`}>
            <span className="sd-conv-msg-icon">{msg.sender === 'customer' ? '👤' : '🤖'}</span>
            <span className="sd-conv-msg-text">{msg.content}</span>
          </div>
        ))}
      </div>

      {/* Status bar — only for order-type conversations */}
      {isOrderType && (
        <div className="sd-conv-status-track">
          {STATUS_FLOW.map((s, i) => (
            <div key={s} className={`sd-conv-status-step ${i <= currentIndex ? 'active' : ''}`}>
              <div className={`sd-conv-status-dot ${i <= currentIndex ? 'sd-dot-active' : 'sd-dot-pending'}`} />
              <span className="sd-conv-status-label">{STATUS_LABELS[s]}</span>
            </div>
          ))}
        </div>
      )}

      {/* Action buttons for order-type */}
      {isOrderType && (
        <div className="sd-conv-actions">
          {canQuote && !showQuoteForm && (
            <button className="sd-conv-quote-btn" onClick={() => setShowQuoteForm(true)}>
              📋 أرسل عرض سعر
            </button>
          )}
          {nextStatus && !canQuote && (
            <button
              className="sd-conv-advance-btn"
              onClick={() => onAdvance(order.id)}
              disabled={advancing === order.id}
            >
              {advancing === order.id ? '...جاري الإرسال' : `⬅ المرحلة التالية: ${STATUS_LABELS[nextStatus]}`}
            </button>
          )}
          {!nextStatus && <div className="sd-conv-complete">✅ تم إكمال الطلب</div>}
        </div>
      )}

      {/* Quote form */}
      {showQuoteForm && (
        <div className="sd-conv-quote-form">
          <h4>📋 عرض سعر — #{order.id}</h4>
          <div className="sd-conv-quote-info">
            <span>المنتج: {order.product}</span>
            <span>التفاصيل: {order.specs}</span>
          </div>
          <div className="sd-conv-quote-fields">
            <div className="sd-conv-quote-row">
              <div className="sd-conv-quote-field">
                <label>سعر الوحدة (ريال)</label>
                <input type="number" value={quoteData.unitPrice} onChange={(e) => setQuoteData({ ...quoteData, unitPrice: e.target.value })} placeholder="85" />
              </div>
              <div className="sd-conv-quote-field">
                <label>الكمية</label>
                <input type="number" value={quoteData.quantity} onChange={(e) => setQuoteData({ ...quoteData, quantity: e.target.value })} placeholder="100" />
              </div>
            </div>
            {quoteData.unitPrice && quoteData.quantity && (
              <div className="sd-conv-quote-total">
                الإجمالي: {(parseFloat(quoteData.unitPrice || '0') * parseInt(quoteData.quantity || '0', 10)).toLocaleString()} ريال
              </div>
            )}
            <div className="sd-conv-quote-field">
              <label>مدة التوصيل</label>
              <input type="text" value={quoteData.delivery} onChange={(e) => setQuoteData({ ...quoteData, delivery: e.target.value })} />
            </div>
            <div className="sd-conv-quote-field">
              <label>ملاحظات (اختياري)</label>
              <input type="text" value={quoteData.notes} onChange={(e) => setQuoteData({ ...quoteData, notes: e.target.value })} placeholder="ضمان سنة، شحن مجاني..." />
            </div>
          </div>
          <div className="sd-conv-quote-btns">
            <button className="sd-conv-quote-send" onClick={handleQuote} disabled={sending}>
              {sending ? '...جاري الإرسال' : 'إرسال العرض'}
            </button>
            <button className="sd-conv-quote-cancel" onClick={() => setShowQuoteForm(false)}>إلغاء</button>
          </div>
        </div>
      )}

      {/* Reply input — always visible */}
      <div className="sd-conv-reply">
        <input
          type="text"
          className="sd-conv-reply-input"
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleReply()}
          placeholder="اكتب رد..."
          dir="rtl"
        />
        <button className="sd-conv-reply-send" onClick={handleReply} disabled={!replyText.trim() || sending}>
          إرسال
        </button>
      </div>
    </div>
  );
}

// ─── Conversations Page ───

function ConversationsPage({ idToken }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('customers');
  const [search, setSearch] = useState('');
  const [advancing, setAdvancing] = useState(null);
  const [sending, setSending] = useState(false);

  const fetchOrders = useCallback(async () => {
    try {
      const res = await fetch(`${WORKER_URL}/api/conversations`, {
        cache: 'no-store',
        headers: { 'Authorization': `Bearer ${idToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setOrders(data);
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
    }
  }, [idToken]);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 5000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const advanceOrder = async (orderId) => {
    setAdvancing(orderId);
    try {
      await fetch(`${WORKER_URL}/api/orders/${orderId}/advance`, { method: 'POST' });
      await fetchOrders();
    } catch (err) { console.error(err); }
    setAdvancing(null);
  };

  const replyToOrder = async (orderId, message) => {
    setSending(true);
    try {
      await fetch(`${WORKER_URL}/api/orders/${orderId}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
      });
      await fetchOrders();
    } catch (err) { console.error(err); }
    setSending(false);
  };

  const sendQuote = async (orderId, quoteData) => {
    setSending(true);
    try {
      await fetch(`${WORKER_URL}/api/orders/${orderId}/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(quoteData),
      });
      await fetchOrders();
    } catch (err) { console.error(err); }
    setSending(false);
  };

  const toggleTest = async (orderId) => {
    try {
      await fetch(`${WORKER_URL}/api/orders/${orderId}/toggle-test`, { method: 'POST' });
      await fetchOrders();
    } catch (err) { console.error(err); }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm('حذف هذه المحادثة؟')) return;
    try {
      await fetch(`${WORKER_URL}/api/orders/${orderId}`, { method: 'DELETE' });
      await fetchOrders();
    } catch (err) { console.error(err); }
  };

  const clearAll = async () => {
    if (!window.confirm('مسح جميع المحادثات؟')) return;
    await fetch(`${WORKER_URL}/api/conversations`, { method: 'DELETE' });
    await fetchOrders();
  };

  // Filter + search
  let filtered = orders;
  if (filter === 'customers') filtered = filtered.filter((o) => !o.isTest);
  if (filter === 'test') filtered = filtered.filter((o) => o.isTest);
  if (search.trim()) {
    const q = search.trim().toLowerCase();
    filtered = filtered.filter((o) =>
      o.phone.includes(q) || o.id.toLowerCase().includes(q) || (o.tag && o.tag.includes(q))
    );
  }

  return (
    <div className="sd-conversations">
      {/* Controls */}
      <div className="sd-conv-controls">
        <div className="sd-conv-filters">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              className={`sd-conv-filter-btn ${filter === f.id ? 'active' : ''}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
              {f.id === 'all' && ` (${orders.length})`}
              {f.id === 'customers' && ` (${orders.filter((o) => !o.isTest).length})`}
              {f.id === 'test' && ` (${orders.filter((o) => o.isTest).length})`}
            </button>
          ))}
        </div>
        <div className="sd-conv-tools">
          <input
            type="text"
            className="sd-conv-search"
            placeholder="🔍 بحث بالرقم أو رقم الطلب..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            dir="rtl"
          />
          <button className="sd-conv-refresh" onClick={fetchOrders}>تحديث 🔄</button>
          <button className="sd-conv-clear" onClick={clearAll}>مسح الكل 🗑️</button>
        </div>
      </div>

      {/* Live indicator */}
      <div className="sd-conv-live">
        <span className="sd-conv-live-dot" />
        <span>يتحدث تلقائياً كل 5 ثواني</span>
      </div>

      {/* Orders list */}
      {loading && <div className="sd-conv-empty">جاري التحميل...</div>}

      {!loading && filtered.length === 0 && (
        <div className="sd-conv-empty">
          {orders.length === 0 ? 'لا توجد محادثات بعد' : 'لا توجد نتائج للفلتر المحدد'}
        </div>
      )}

      <div className="sd-conv-list">
        {filtered.map((order) => (
          <OrderCard
            key={order.id}
            order={order}
            onAdvance={advanceOrder}
            onReply={replyToOrder}
            onQuote={sendQuote}
            onToggleTest={toggleTest}
            onDelete={deleteOrder}
            advancing={advancing}
            sending={sending}
          />
        ))}
      </div>
    </div>
  );
}

export default ConversationsPage;
